import { useMemo } from 'react';

import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import { Skeleton } from '@/components/ui/skeleton';
import { useJobStats } from '@/hooks/useJobs';

// How many performers to list.
const TOP_N = 5;

function initials(name: string) {
  return name
    .split(' ')
    .map((part) => part[0])
    .join('')
    .slice(0, 2)
    .toUpperCase();
}

/** 'job_scout' → 'Job Scout'. */
function humanizeRole(role: string): string {
  return role
    .split('_')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
}

export function TopPerformers() {
  const { data, isLoading, isError } = useJobStats();

  const performers = useMemo(() => {
    if (!data?.byEmployee) return [];
    const ranked = data.byEmployee
      .filter((e) => e.count > 0)
      .slice(0, TOP_N);
    const leader = ranked[0]?.count ?? 0;
    return ranked.map((e) => ({
      id: e.employeeId,
      name: e.employeeName,
      role: humanizeRole(e.employeeRole),
      jobs: e.count,
      progress: leader > 0 ? Math.round((e.count / leader) * 100) : 0,
    }));
  }, [data]);

  return (
    <Card>
      <CardHeader>
        <CardTitle>Top Performers</CardTitle>
        <CardDescription>Most jobs posted</CardDescription>
      </CardHeader>
      <CardContent>
        {isLoading ? (
          <ul className="space-y-4">
            {Array.from({ length: 4 }).map((_, i) => (
              <li key={i} className="flex items-center gap-3">
                <Skeleton className="size-9 rounded-full" />
                <div className="flex-1 space-y-2">
                  <Skeleton className="h-4 w-32" />
                  <Skeleton className="h-3 w-20" />
                </div>
              </li>
            ))}
          </ul>
        ) : isError ? (
          <p className="py-12 text-center text-sm text-muted-foreground">
            Couldn’t load top performers.
          </p>
        ) : performers.length === 0 ? (
          <p className="py-12 text-center text-sm text-muted-foreground">No jobs posted yet.</p>
        ) : (
          <ul className="space-y-4">
            {performers.map((person, index) => (
              <li key={person.id} className="flex items-center gap-3">
                <span className="w-4 text-sm font-medium text-muted-foreground tabular-nums">
                  {index + 1}
                </span>
                <Avatar className="size-9">
                  <AvatarFallback className="bg-primary/10 text-xs font-medium text-primary">
                    {initials(person.name)}
                  </AvatarFallback>
                </Avatar>
                <div className="min-w-0 flex-1">
                  <p className="truncate text-sm font-medium">{person.name}</p>
                  <p className="truncate text-xs text-muted-foreground">{person.role}</p>
                </div>
                <span className="text-sm font-semibold tabular-nums">{person.jobs}</span>
              </li>
            ))}
          </ul>
        )}
      </CardContent>
    </Card>
  );
}
